/**
 * React Query hooks for Webhook Configuration
 *
 * Per Spec 0017 Phase 5 + Design 5.4
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listWebhookConfigs,
  getWebhookConfig,
  setWebhookConfig,
  deleteWebhookConfig,
  rotateWebhookSecret,
  testWebhook,
} from '@/lib/api/webhook-config';
import type {
  WebhookConfig,
  WebhookTestResult,
  WebhookConfigListResponse,
} from '@/types/webhook-config';

/** Mask cleartext secret before caching in React Query. */
const SECRET_MASK = 'whsec_****...XXXX';

function sanitizeForCache(config: WebhookConfig): WebhookConfig {
  if (!config.hmacSecret || config.hmacSecret.includes('****')) {
    return config;
  }
  return { ...config, hmacSecret: SECRET_MASK };
}

/** Query key factory for webhook configs */
export const webhookConfigKeys = {
  all: ['webhook-configs'] as const,
  lists: () => [...webhookConfigKeys.all, 'list'] as const,
  list: (params?: { enabled?: boolean; cursor?: string }) =>
    [...webhookConfigKeys.lists(), params] as const,
  details: () => [...webhookConfigKeys.all, 'detail'] as const,
  detail: (merchantId: string) => [...webhookConfigKeys.details(), merchantId] as const,
};

/**
 * Fetch paginated list of webhook configs.
 */
export function useWebhookConfigs(params?: {
  enabled?: boolean;
  cursor?: string;
  limit?: number;
}) {
  return useQuery<WebhookConfigListResponse, Error>({
    queryKey: webhookConfigKeys.list(params),
    queryFn: () => listWebhookConfigs(params),
    staleTime: 30_000,
  });
}

/**
 * Fetch webhook config for a specific merchant.
 */
export function useWebhookConfig(merchantId: string) {
  return useQuery<WebhookConfig, Error>({
    queryKey: webhookConfigKeys.detail(merchantId),
    queryFn: () => getWebhookConfig(merchantId),
    enabled: !!merchantId,
  });
}

/**
 * Set or update webhook config for a merchant.
 * Sanitizes cleartext secret before caching — the raw response
 * (with cleartext on 201) is only available via mutation.data or onSuccess.
 */
export function useSetWebhookConfig() {
  const queryClient = useQueryClient();

  return useMutation<
    WebhookConfig,
    Error,
    { merchantId: string; webhookUrl: string }
  >({
    mutationFn: ({ merchantId, webhookUrl }) =>
      setWebhookConfig(merchantId, { webhookUrl }),
    onSuccess: (config) => {
      queryClient.setQueryData(
        webhookConfigKeys.detail(config.merchantId),
        sanitizeForCache(config)
      );
      queryClient.invalidateQueries({ queryKey: webhookConfigKeys.lists() });
    },
  });
}

/**
 * Deactivate webhook config.
 */
export function useDeleteWebhookConfig() {
  const queryClient = useQueryClient();

  return useMutation<WebhookConfig, Error, string>({
    mutationFn: deleteWebhookConfig,
    onSuccess: (config) => {
      queryClient.setQueryData(webhookConfigKeys.detail(config.merchantId), config);
      queryClient.invalidateQueries({ queryKey: webhookConfigKeys.lists() });
    },
  });
}

/**
 * Rotate HMAC secret (returns cleartext new secret).
 * Sanitizes cleartext before caching — cleartext only available
 * via mutation.data or the caller's onSuccess callback.
 */
export function useRotateWebhookSecret() {
  const queryClient = useQueryClient();

  return useMutation<WebhookConfig, Error, string>({
    mutationFn: rotateWebhookSecret,
    onSuccess: (config) => {
      queryClient.setQueryData(
        webhookConfigKeys.detail(config.merchantId),
        sanitizeForCache(config)
      );
      queryClient.invalidateQueries({ queryKey: webhookConfigKeys.lists() });
    },
  });
}

/**
 * Send test webhook.
 */
export function useTestWebhook() {
  return useMutation<WebhookTestResult, Error, string>({
    mutationFn: testWebhook,
  });
}
